import { useState } from "react";
import {
  Clock,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Layers,
  Compass,
  FileText,
  History,
  Paperclip,
  Bookmark,
  Sparkles,
  PackagePlus,
  Ban,
  XCircle,
  ShieldAlert,
  Info,
} from "lucide-react";
import {
  Card,
  KpiCard,
  KpiGrid,
  type KpiItem,
  Tabs,
  TabPanel,
  type TabItem,
  Pagination,
  Badge,
  SearchAppendRepeater,
  type SearchAppendOption,
  type SearchAppendLineItem,
} from "@/shared/ui";
import { Breadcrumb, type BreadcrumbItem } from "@/shared/ui/layout/Breadcrumb";

import { TestUiCoreTimelineShowcase } from "./TestUiCoreTimelineShowcase";

export function TestUiCoreNavigationCards() {
  // 1. KPI Items State (Đúng chuẩn Design System: viền 2px tone, dot active indicator, nền pastel nhạt khi chưa chọn, saturated bg khi active)
  const [selectedKpi, setSelectedKpi] = useState<string>("total_docs");

  const kpiItems: KpiItem[] = [
    {
      id: "total_docs",
      label: "Tổng số phiếu",
      badge: "Toàn bộ",
      value: "158",
      hint: "Quy tắc: Tổng là màu trung tính",
      tone: "neutral",
      icon: Layers,
      selected: selectedKpi === "total_docs",
      onClick: () => setSelectedKpi("total_docs"),
    },
    {
      id: "in_production",
      label: "Đang sản xuất / Gom",
      badge: "Đang chạy",
      icon: Clock,
      value: "34",
      hint: "Quy tắc: Đang SX là màu lime",
      tone: "accent",
      selected: selectedKpi === "in_production",
      onClick: () => setSelectedKpi("in_production"),
    },
    {
      id: "packed_done",
      label: "Hoàn tất đóng thùng",
      badge: "Đạt QC",
      icon: CheckCircle2,
      value: "119",
      hint: "Quy tắc: Hoàn thành là màu xanh lá",
      tone: "success",
      selected: selectedKpi === "packed_done",
      onClick: () => setSelectedKpi("packed_done"),
    },
    {
      id: "warning_stock",
      label: "Cảnh báo / Tồn thấp",
      badge: "Gần hạn",
      icon: AlertTriangle,
      value: "8",
      hint: "Quy tắc: Cảnh báo là màu cam",
      tone: "warning",
      selected: selectedKpi === "warning_stock",
      onClick: () => setSelectedKpi("warning_stock"),
    },
    {
      id: "rejected_docs",
      label: "Đã hủy / Bãi bỏ",
      badge: "Đã hủy",
      icon: XCircle,
      value: "3",
      hint: "Quy tắc: Status đã hủy là màu nóng",
      tone: "danger",
      selected: selectedKpi === "rejected_docs",
      onClick: () => setSelectedKpi("rejected_docs"),
    },
    {
      id: "qa_hold",
      label: "Tạm giữ QC / CCP",
      badge: "Niêm phong",
      icon: ShieldAlert,
      value: "2",
      hint: "Quy tắc: Tạm giữ QC kế thừa danger",
      tone: "hold",
      selected: selectedKpi === "qa_hold",
      onClick: () => setSelectedKpi("qa_hold"),
    },
    {
      id: "draft_docs",
      label: "Chờ duyệt / Nháp",
      badge: "Tạm lưu",
      icon: FileText,
      value: "6",
      hint: "Quy tắc: Chờ, nháp là màu xám",
      tone: "neutral",
      selected: selectedKpi === "draft_docs",
      onClick: () => setSelectedKpi("draft_docs"),
    },
    {
      id: "info_docs",
      label: "Chỉ dẫn vận hành",
      badge: "SOP",
      icon: Info,
      value: "12",
      hint: "Quy tắc: Ghi chú, thông tin là info",
      tone: "info",
      selected: selectedKpi === "info_docs",
      onClick: () => setSelectedKpi("info_docs"),
    },
    {
      id: "brand_focus",
      label: "Tiêu điểm TM Food",
      badge: "Brand",
      icon: Sparkles,
      value: "100%",
      hint: "Quy tắc: Thương hiệu là primary",
      tone: "primary",
      selected: selectedKpi === "brand_focus",
      onClick: () => setSelectedKpi("brand_focus"),
    },
  ];

  // 2. Breadcrumbs State
  const breadcrumb1: BreadcrumbItem[] = [
    { label: "Trang chủ", to: "/" },
    { label: "Quản lý sản xuất", to: "/testui" },
    { label: "Kế hoạch tuần 40", to: "/testui" },
    { label: "Lô kiểm nghiệm #LK-2026-001" },
  ];

  const breadcrumb2: BreadcrumbItem[] = [
    { label: "Kho vận TM Food", to: "/testui" },
    { label: "Phiếu nhập đông lạnh #NK-089" },
  ];

  // 3. Tabs State
  const [activeTab, setActiveTab] = useState("general");
  const tabItems: TabItem[] = [
    { value: "general", label: "1. Thông tin chung" },
    { value: "items", label: "2. Danh mục mặt hàng (12)" },
    { value: "logs", label: "3. Lịch sử duyệt ký" },
    { value: "attachments", label: "4. Chứng từ & Ảnh (3)" },
    { value: "disabled_tab", label: "5. Tạm khóa", disabled: true },
  ];

  // 4. Pagination State
  const [page, setPage] = useState(2);
  const [pageSize, setPageSize] = useState(10);
  const [simplePage, setSimplePage] = useState(1);
  const totalItems = 158;

  // 5. Card Interactive Selection
  const [selectedCardId, setSelectedCardId] = useState<string>("card-1");

  // 6. Search + Append Repeater Demo State
  const demoCatalog: SearchAppendOption[] = [
    {
      value: "NVL-CT-RAW",
      label: "Cá tra nguyên con tươi sống loại 1 (0.8 - 1.2kg)",
      subLabel: "Nguyên liệu đầu vào · Đạt chuẩn VietGAP",
      uom: "kg",
      defaultQty: 3000,
      spec: "Sống tại bè, test kháng sinh âm tính",
    },
    {
      value: "NVL-TS-RAW",
      label: "Tôm sú sống tự nhiên Bạc Liêu size 20-25 con/kg",
      subLabel: "Thu hoạch trong ngày · Cấp lạnh 0-4°C",
      uom: "kg",
      defaultQty: 800,
      spec: "Ướp đá lạnh giữ tươi, không tạp chất",
    },
    {
      value: "BB-THUNG-10KG",
      label: "Thùng carton 5 lớp xuất khẩu chống thấm 10kg",
      subLabel: "Bao bì đóng gói · Chuẩn in Offset TM Food",
      uom: "thùng",
      defaultQty: 500,
      spec: "Sóng BC chịu lực nén 450kg",
    },
    {
      value: "BB-TUI-PE-1KG",
      label: "Túi PE dẻo in thương hiệu xuất khẩu 1kg",
      subLabel: "Bao bì cấp 1 tiếp xúc trực tiếp thực phẩm",
      uom: "hộp",
      defaultQty: 1000,
      spec: "Độ dày 0.08mm, đạt chứng nhận BRC",
    },
  ];

  const [demoLines, setDemoLines] = useState<SearchAppendLineItem[]>([
    {
      id: "demo-1",
      itemCode: "NVL-CT-RAW",
      itemName: "Cá tra nguyên con tươi sống loại 1 (0.8 - 1.2kg)",
      qty: 3000,
      uom: "kg",
      spec: "Sống tại bè, test kháng sinh âm tính",
    },
    {
      id: "demo-2",
      itemCode: "BB-THUNG-10KG",
      itemName: "Thùng carton 5 lớp xuất khẩu chống thấm 10kg",
      qty: 300,
      uom: "thùng",
      spec: "Sóng BC chịu lực nén 450kg",
    },
  ]);

  return (
    <div className="space-y-6">
      {/* 1. KPI Cards */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3 mb-4">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Compass className="size-5 text-primary" />
              1. Thẻ Chỉ Số Nghiệp Vụ (KpiCard & KpiGrid)
            </h3>
            <p className="mt-1 mb-0 text-caption text-text-muted">
              Được dùng tại đầu trang Dashboard, danh sách và tổng quan phân xưởng. Hỗ trợ bấm tương tác lọc dữ liệu (viền 2px tone, chấm active indicator, nền pastel nhạt khi chưa chọn, saturated bg khi active, thẻ Inactive giữ đúng tone neutral xám).
            </p>
          </div>
          {selectedKpi && (
            <Badge
              tone={kpiItems.find((k) => k.id === selectedKpi)?.tone || "primary"}
              label={`Đang lọc: ${kpiItems.find((k) => k.id === selectedKpi)?.label || selectedKpi}`}
            />
          )}
        </div>

        <KpiGrid items={kpiItems} />
      </section>

      {/* 2. Breadcrumbs & Header Layout */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Compass className="size-5 text-primary" />
            2. Điều Hướng Phân Cấp (Breadcrumb)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Hiển thị vị trí trang hiện tại theo sơ đồ phân cấp (breadcrumb), hỗ trợ liên kết nội bộ và tối ưu a11y.
          </p>
        </div>

        <div className="space-y-4">
          <div className="rounded-control border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="text-caption font-semibold text-text-muted uppercase tracking-wider">
              Ví dụ 1: Đường dẫn 4 cấp (Nhận diện mã thực thể & Icon Home)
            </div>
            <Breadcrumb items={breadcrumb1} />
          </div>

          <div className="rounded-control border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="text-caption font-semibold text-text-muted uppercase tracking-wider">
              Ví dụ 2: Dạng Pill nổi (variant="pill")
            </div>
            <Breadcrumb items={breadcrumb2} variant="pill" />
          </div>

          <div className="rounded-control border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="text-caption font-semibold text-text-muted uppercase tracking-wider">
              Ví dụ 3: Thu gọn thông minh khi nhiều cấp (maxItems=3, click "..." để mở)
            </div>
            <Breadcrumb
              items={[
                { label: "Ops", to: "/testui" },
                { label: "Kế hoạch", to: "/testui" },
                { label: "Lệnh sản xuất", to: "/testui" },
                { label: "Công đoạn sấy", to: "/testui" },
                { label: "MO-202609-001" },
              ]}
              maxItems={3}
            />
          </div>
        </div>
      </section>

      {/* 3. Radix Tabs */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            3. Thanh Chuyển Tab Radix (Tabs & TabPanel)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Xây dựng trên Radix UI Tabs với điều khiển trượt ngang mượt mà, menu tràn tự động và chuyển đổi nội dung liền mạch.
          </p>
        </div>

        <div className="rounded-control border border-border/70 bg-bg p-4">
          <Tabs items={tabItems} value={activeTab} onValueChange={setActiveTab}>
            <TabPanel value="general">
              <div className="rounded-control border border-border bg-surface p-4 text-text space-y-2">
                <div className="font-bold flex items-center gap-2 text-primary">
                  <FileText className="size-4" />
                  Nội dung Tab 1: Thông tin chung lô hàng
                </div>
                <p className="text-body text-text-secondary m-0">
                  Mã lô: <strong>LK-2026-001</strong> | Dòng sản phẩm: Fillet Cá Tra Xuất Khẩu loại 1 | Nhiệt độ bảo quản: -18°C.
                </p>
              </div>
            </TabPanel>

            <TabPanel value="items">
              <div className="rounded-control border border-border bg-surface p-4 text-text space-y-2">
                <div className="font-bold flex items-center gap-2 text-primary">
                  <Bookmark className="size-4" />
                  Nội dung Tab 2: Danh mục mặt hàng chi tiết
                </div>
                <p className="text-body text-text-secondary m-0">
                  Tổng cộng có 12 mặt hàng trong lô, bao gồm quy cách 500g, 1kg và thùng carton 10kg đóng gói tiêu chuẩn EU.
                </p>
              </div>
            </TabPanel>

            <TabPanel value="logs">
              <div className="rounded-control border border-border bg-surface p-4 text-text space-y-2">
                <div className="font-bold flex items-center gap-2 text-primary">
                  <History className="size-4" />
                  Nội dung Tab 3: Lịch sử phê duyệt ký số
                </div>
                <p className="text-body text-text-secondary m-0">
                  10:15 - KCS Nhà máy kiểm định đạt chuẩn | 10:45 - Trưởng ca ký xác nhận số lượng | 11:30 - QA duyệt xuất xưởng.
                </p>
              </div>
            </TabPanel>

            <TabPanel value="attachments">
              <div className="rounded-control border border-border bg-surface p-4 text-text space-y-2">
                <div className="font-bold flex items-center gap-2 text-primary">
                  <Paperclip className="size-4" />
                  Nội dung Tab 4: Chứng chỉ & Hình ảnh đính kèm
                </div>
                <p className="text-body text-text-secondary m-0">
                  Có 3 tệp đính kèm: Giấy chứng nhận HACCP, Phiếu kiểm nghiệm vi sinh và Biên bản bàn giao ca.
                </p>
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </section>

      {/* 4. Pagination */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Sparkles className="size-5 text-primary" />
            4. Phân Trang Danh Sách (Pagination)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Hỗ trợ phân trang dạng số đầy đủ (numbered) kèm chọn số bản ghi mỗi trang, và dạng rút gọn (simple) cho thiết bị di động.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-caption font-semibold text-text-secondary">
              Dạng Số (Numbered) + Tùy chọn 5 / 10 / 20 / 50 dòng/trang:
            </div>
            <div className="rounded-control border border-border bg-bg p-3">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={totalItems}
                onPageChange={setPage}
                pageSizeOptions={[5, 10, 20, 50]}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-caption font-semibold text-text-secondary">
              Dạng Đơn Giản (Simple) thích hợp drawer hoặc mobile:
            </div>
            <div className="rounded-control border border-border bg-bg p-3">
              <Pagination
                variant="simple"
                page={simplePage}
                pageSize={10}
                total={50}
                onPageChange={setSimplePage}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 5. Surface Cards */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            5. Thẻ Chứa Nội Dung Đa Trạng Thái (Card)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Khung chứa nội dung cơ bản với các chế độ: Padded, Dense, Interactive (hover nhấc nhẹ) và Selected (viền ring tiêu điểm).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            interactive
            selected={selectedCardId === "card-1"}
            onClick={() => setSelectedCardId("card-1")}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-text">Thẻ Tương Tác 1</span>
                {selectedCardId === "card-1" && <Badge tone="primary" label="Đang chọn" />}
              </div>
              <p className="text-caption text-text-secondary m-0">
                Thẻ có hiệu ứng hover nhấc nhẹ và đổ bóng chuẩn ERP. Bấm vào để kích hoạt viền chọn.
              </p>
            </div>
          </Card>

          <Card
            interactive
            selected={selectedCardId === "card-2"}
            onClick={() => setSelectedCardId("card-2")}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-text">Thẻ Tương Tác 2</span>
                {selectedCardId === "card-2" && <Badge tone="primary" label="Đang chọn" />}
              </div>
              <p className="text-caption text-text-secondary m-0">
                Tích hợp sẵn các thuộc tính accessibility, hỗ trợ điều hướng bàn phím focus-visible.
              </p>
            </div>
          </Card>

          <Card dense className="bg-primary-soft/30 border-primary/40">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-primary">Thẻ Compact Dense</span>
                <Badge tone="success" label="Rút gọn" />
              </div>
              <p className="text-caption text-text-secondary m-0">
                Padding p-3 tiết kiệm không gian dành cho bảng tóm tắt nhanh hoặc sidebar.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* 6. Search + Append Repeater */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex items-center justify-between">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <PackagePlus className="size-5 text-primary" />
              6. Bộ Nhập Dòng Hàng Siêu Tốc (SearchAppendRepeater)
            </h3>
            <Badge tone="primary" label="Search + Append Pattern" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thay thế hoàn toàn mô hình cũ (phải bấm thêm dòng rồi mới mở dropdown tìm kiếm).
            Với <strong>Search + Append</strong>, người dùng gõ tìm hoặc quét mã barcode là mặt hàng được đẩy ngay vào bảng,
            tự động cộng dồn số lượng khi chọn trùng và giữ con trỏ phím liên tục để nhập hàng loạt.
          </p>
        </div>

        <SearchAppendRepeater
          items={demoLines}
          onChange={setDemoLines}
          options={demoCatalog}
          title="Danh mục vật tư / nguyên liệu cấp phát"
          searchPlaceholder="Gõ mã SKU hoặc tên nguyên vật liệu để thêm vào danh sách... (/)"
          summaryUnitLabel="kg"
        />
      </section>

      {/* 7. Vertical Timeline / Progress Tracker */}
      <TestUiCoreTimelineShowcase />
    </div>
  );
}
